import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, message } from 'antd';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import TitleText from '../../atoms/TitleText';
import FormField from '../../FormField/FormField';
import InfoText from '../../atoms/InfoText';
import Button from '../../atoms/Button';
import { useDeleteReviewMutation } from '../../../store/services/Review';

export default function DeleteFeedback({
  isModalOpen,
  handleCloseModal,
  reviewId,
  refetch,
  refetchCallBack,
}: {
  isModalOpen: boolean;
  handleCloseModal: () => void;
  reviewId: string;
  refetch: () => Promise<{ data?: unknown; error?: unknown }>;
  refetchCallBack: () => void;
}) {
  const { t } = useTranslation();
  const required_error = t('components.validations.minLength', { value: 2 });
  const max_size_error = t('components.validations.maxLength', { value: 50 });

  const [deleteReview, { isLoading }] = useDeleteReviewMutation();

  const ZodSchema = z.object({
    comments: z.string().min(2, required_error).max(50, max_size_error),
  });
  type formField = z.infer<typeof ZodSchema>;

  const methods = useForm<formField>({
    mode: 'all',
    resolver: zodResolver(ZodSchema),
    defaultValues: { comments: '' },
  });

  const onSubmit = async () => {
    try {
      await deleteReview({
        reviewId: reviewId,
        comments: methods.getValues().comments,
      }).unwrap();

      message.success(t('reviews.deleteSuccess', 'Feedback deleted successfully'));

      refetchCallBack?.();
      handleCloseModal?.();

      await refetch?.();
    } catch {
      message.error(t('reviews.deleteError', 'Failed to delete feedback'));
    }
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={() => handleCloseModal?.()}
      closable={false}
      footer={null}
      centered
      destroyOnClose={true}
      width={716}
      data-testid="delete-feedback-modal"
    >
      <div className="flex flex-col gap-6 py-4 px-2" data-testid="delete-feedback-modal-content">
        <div className="flex flex-col gap-2" data-testid="delete-feedback-header-container">
          <TitleText
            text={t('reviews.deleteFeedback')}
            size="2xl"
            className="!text-[#111928] !font-semibold "
          />
          <InfoText
            text={t('reviews.deleteFeedback subTitle')}
            size="sm"
            className="w-11/12 !text-[#637381] dark:!text-dark-text-secondary"
          />
        </div>

        <FormProvider {...methods}>
          <form className="w-full" onSubmit={methods.handleSubmit(onSubmit)} data-testid="delete-feedback-form">
            <div className="w-full h-60" data-testid="delete-feedback-textarea-container">
              <FormField
                id="comments"
                name="comments"
                type="textarea"
                placeholder={t('reviews.reasonForCommentDeletion')}
                data-testid="delete-feedback-modal-comments-textarea"
                className="w-full !h-40 bg-transparent"
                maxLength={50}
                label={t('reviews.reasonForCommentDeletion')}
                helperText={methods.formState.errors.comments?.message || t('dispute.messageInterview', {
                  min: 2,
                  max: 50,
                })}
              />
            </div>
            <div className="flex items-center justify-end w-full gap-6" data-testid="delete-feedback-actions-container">
              <Button
                type="button"
                onClick={() => handleCloseModal?.()}
                variant="outline"
                data-testid="delete-feedback-cancel-btn"
              >
                {t('home.cancel')}
              </Button>

              <Button type="submit" disabled={isLoading} data-testid="delete-feedback-submit-btn">
                {t('yes')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
}
